import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchTppEventProofBlob } from '../../../services/transitPassPool.service';
import {
  tppProofDisplayKind,
  type TppProofDisplayKind,
} from '../../../services/systems/transit-pass-pool/tppProofFile';

type ProofViewerState = {
  eventId: number;
  url: string;
  displayKind: TppProofDisplayKind;
  fileName: string | null;
};

/** 利用証明の取得・Object URL 解放 */
export function useTppProofViewer() {
  const { t } = useTranslation();
  const [proofViewer, setProofViewer] = useState<ProofViewerState | null>(null);
  const [proofViewerLoading, setProofViewerLoading] = useState(false);
  const [proofViewError, setProofViewError] = useState<string | null>(null);
  const proofViewerUrlRef = useRef<string | null>(null);

  useEffect(() => {
    proofViewerUrlRef.current = proofViewer?.url ?? null;
  });

  useEffect(() => {
    return () => {
      if (proofViewerUrlRef.current) {
        URL.revokeObjectURL(proofViewerUrlRef.current);
      }
    };
  }, []);

  const closeProofViewer = useCallback(() => {
    if (proofViewer?.url) {
      URL.revokeObjectURL(proofViewer.url);
    }
    setProofViewer(null);
    setProofViewError(null);
    setProofViewerLoading(false);
  }, [proofViewer?.url]);

  const openProofViewer = useCallback(
    async (eventId: number) => {
      if (proofViewer?.url) {
        URL.revokeObjectURL(proofViewer.url);
      }
      setProofViewer(null);
      setProofViewerLoading(true);
      setProofViewError(null);
      try {
        const blob = await fetchTppEventProofBlob(eventId);
        const url = URL.createObjectURL(blob);
        setProofViewer({
          eventId,
          url,
          displayKind: tppProofDisplayKind(blob.type),
          fileName: null,
        });
      } catch {
        setProofViewError(t('tpp.proof_load_failed'));
      } finally {
        setProofViewerLoading(false);
      }
    },
    [proofViewer?.url, t]
  );

  return {
    proofViewer,
    proofViewerLoading,
    proofViewError,
    openProofViewer,
    closeProofViewer,
  };
}
